<template>
  <div class="card">
    <b-card-header>
      <h4 class="mb-0">
        {{ $t('usersettings.reportingApiKey') }}
      </h4>
    </b-card-header>
    <b-card-body>
      <table
        v-if="reportingApiKey.exists"
        class="table b-table table-striped mb-3"
      >
        <tbody>
          <tr>
            <td><strong>{{ $t('apps.status') }}</strong></td>
            <td>
              <b-badge :variant="reportingApiKey.status === 'active' ? 'success' : 'danger'">
                {{ reportingApiKey.status === 'active' ? $t('usersettings.active') : $t('usersettings.inactive') }}
              </b-badge>
            </td>
          </tr>
          <tr>
            <td><strong>{{ $t('usersettings.lastUsed') }}</strong></td>
            <td>
              <span v-if="reportingApiKey.last_used_at">{{ reportingApiKey.last_used_at | dotDateTimeShort }}</span>
              <span v-else>{{ $t('usersettings.never') }}</span>
            </td>
          </tr>
        </tbody>
      </table>
      <p
        v-else
        class="text-muted"
      >
        {{ $t('usersettings.noReportingApiKeyHasBeenGeneratedForThis') }}
      </p>

      <div>
        <b-button
          v-if="!reportingApiKey.exists"
          class="d-inline-block mr-1"
          variant="success"
          :disabled="isRequestPending"
          @click="generateKey"
        >
          {{ $t('usersettings.generateApiKey') }}
        </b-button>
        <template v-else>
          <b-button
            class="d-inline-block mr-1"
            variant="outline-primary"
            :disabled="isRequestPending"
            @click="rotateKey"
          >
            {{ $t('usersettings.rotateKey') }}
          </b-button>
          <b-button
            v-if="reportingApiKey.status === 'active'"
            class="d-inline-block"
            variant="outline-danger"
            :disabled="isRequestPending"
            @click="deactivateKey"
          >
            {{ $t('usersettings.deactivate') }}
          </b-button>
          <b-button
            v-else
            class="d-inline-block"
            variant="outline-success"
            :disabled="isRequestPending"
            @click="activateKey"
          >
            {{ $t('usersettings.activate') }}
          </b-button>
        </template>
      </div>
    </b-card-body>

    <b-modal
      v-model="isRevealModalVisible"
      :title="$t('usersettings.reportingApiKey')"
      hide-footer
      no-close-on-backdrop
      @hidden="newKey = ''"
    >
      <b-alert
        show
        variant="warning"
      >
        {{ $t('usersettings.copyThisKeyNowForSecurityReasonsItWill') }}
      </b-alert>
      <b-input-group>
        <b-form-input
          :value="newKey"
          readonly
        />
        <b-input-group-append>
          <b-button
            variant="outline-success"
            @click="copyText(newKey)"
          >
            {{ $t('apps.copy') }}
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="secondary"
          @click="isRevealModalVisible = false"
        >
          {{ $t('usersettings.close') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import i18n from '@/libs/i18n'

export default {
  data() {
    return {
      reportingApiKey: {
        exists: false,
        status: null,
        last_used_at: null,
      },
      isRevealModalVisible: false,
      newKey: '',
      isRequestPending: false,
    }
  },
  created() { this.fetchKey() },
  methods: {
    fetchKey() {
      this.showLoading()
      this.$http.get('/api/profile-settings/reporting-api-key').then(res => {
        this.reportingApiKey = res.data.reportingApiKey || {
          exists: false,
          status: null,
          last_used_at: null,
        }
      })
        .catch(errors => {
          this.alertApiError(errors, i18n.t('usersettings.failedToLoadReportingApiKey'))
        })
        .finally(() => {
          this.hideLoading()
        })
    },
    generateKey() {
      if (this.isRequestPending) return
      this.isRequestPending = true
      this.showLoading()
      this.$http.post('/api/profile-settings/reporting-api-key/generate').then(res => {
        this.newKey = res.data.key
        this.reportingApiKey = res.data.reportingApiKey
        this.isRevealModalVisible = true
        this.alertSuccess(i18n.t('usersettings.reportingApiKeyGeneratedSuccessfully'))
      })
        .catch(errors => {
          this.alertApiError(errors, i18n.t('usersettings.failedToGenerateReportingApiKey'))
        })
        .finally(() => {
          this.hideLoading()
          this.isRequestPending = false
        })
    },
    rotateKey() {
      if (this.isRequestPending) return
      this.isRequestPending = true

      this.$bvModal.msgBoxConfirm(
        i18n.t('usersettings.rotatingTheKeyWillImmediatelyInvalidateTheCurrentReporting'),
        {
          title: i18n.t('usersettings.rotateApiKey'),
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: i18n.t('usersettings.rotate'),
          cancelTitle: i18n.t('usersettings.cancel'),
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        },
      ).then(value => {
        if (!value) {
          this.isRequestPending = false
          return
        }

        this.showLoading()
        this.$http.post('/api/profile-settings/reporting-api-key/rotate').then(res => {
          this.newKey = res.data.key
          this.reportingApiKey = res.data.reportingApiKey
          this.isRevealModalVisible = true
          this.alertSuccess(i18n.t('usersettings.reportingApiKeyRotatedSuccessfully'))
        })
          .catch(errors => {
            this.alertApiError(errors, i18n.t('usersettings.failedToRotateReportingApiKey'))
          })
          .finally(() => {
            this.hideLoading()
            this.isRequestPending = false
          })
      }).catch(() => {
        this.isRequestPending = false
      })
    },
    deactivateKey() {
      if (this.isRequestPending) return
      this.isRequestPending = true

      this.$bvModal.msgBoxConfirm(
        i18n.t('usersettings.deactivatingTheKeyWillImmediatelyStopItFromWorking'),
        {
          title: i18n.t('usersettings.deactivateApiKey'),
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: i18n.t('usersettings.deactivate'),
          cancelTitle: i18n.t('usersettings.cancel'),
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        },
      ).then(value => {
        if (!value) {
          this.isRequestPending = false
          return
        }

        this.showLoading()
        this.$http.patch('/api/profile-settings/reporting-api-key/deactivate').then(res => {
          this.reportingApiKey = res.data.reportingApiKey
          this.alertSuccess(i18n.t('usersettings.reportingApiKeyDeactivatedSuccessfully'))
        })
          .catch(errors => {
            this.alertApiError(errors, i18n.t('usersettings.failedToDeactivateReportingApiKey'))
          })
          .finally(() => {
            this.hideLoading()
            this.isRequestPending = false
          })
      }).catch(() => {
        this.isRequestPending = false
      })
    },
    activateKey() {
      if (this.isRequestPending) return
      this.isRequestPending = true
      this.showLoading()
      this.$http.patch('/api/profile-settings/reporting-api-key/activate').then(res => {
        this.reportingApiKey = res.data.reportingApiKey
        this.alertSuccess(i18n.t('usersettings.reportingApiKeyActivatedSuccessfully'))
      })
        .catch(errors => {
          this.alertApiError(errors, i18n.t('usersettings.failedToActivateReportingApiKey'))
        })
        .finally(() => {
          this.hideLoading()
          this.isRequestPending = false
        })
    },
  },
}
</script>
